import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../store/UserContext';
import { t } from '../i18n';
import { useTheme } from '../hooks/useTheme';

const PageLoader = ({ children }) => {
  const { user, isInitialized } = useUser();
  const location = useLocation();
  const { language } = useTheme();
  const [isPageReady, setIsPageReady] = useState(false);
  const [pageLoadError, setPageLoadError] = useState(null);
  const [loadingMessageKey, setLoadingMessageKey] = useState('page_loader_loading');

  useEffect(() => {
    const initializePage = () => {
      try {
        setPageLoadError(null);
        setLoadingMessageKey('page_loader_check_status');

        // 等待用户状态初始化完成（UserContext 已负责从 localStorage 恢复）
        if (!isInitialized) {
          return;
        }

        setLoadingMessageKey('page_loader_check_permissions');

        // 未登录仅放行登录相关页；/ 与 /welcome 由路由重定向到 /login，不作为访客公开壳
        const publicPaths = ['/', '/login', '/register', '/reset-password', '/auth/alipay/callback'];
        const isPublicPath = publicPaths.includes(location.pathname);

        // 商城页面即使未登录也不重定向到登录页面
        const isMallPage = location.pathname.startsWith('/plant-protection-mall') || location.pathname.startsWith('/mall');

        if (!isPublicPath && !user && !isMallPage) {
          setLoadingMessageKey('page_loader_redirect_login');
          setTimeout(() => {
            window.location.href = '/login';
          }, 1000);
          return;
        }

        setLoadingMessageKey('page_loader_ready');
        // 不再人为延迟 500ms，初始化完成后立即放行
        setIsPageReady(true);
      } catch (error) {
        console.error('页面初始化失败:', error);
        setPageLoadError(error.message || t('page_loader_default_error'));
      }
    };

    initializePage();
  }, [location.pathname, user, isInitialized, language]);

  const loadingMessage = t(loadingMessageKey);

  // 显示加载状态
  if (!isInitialized || !isPageReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{loadingMessage}</p>
          <div className="mt-2 text-sm text-gray-500">
            {t('page_loader_processing')}
          </div>
        </div>
      </div>
    );
  }

  // 显示错误状态
  if (pageLoadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-white">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
            {t('page_loader_error_title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {pageLoadError || t('page_loader_default_error')}
          </p>
          <div className="space-y-2">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {t('page_loader_reload')}
            </button>
            <button
              onClick={() => window.location.href = '/welcome'}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              {t('page_loader_back_home')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default PageLoader;
